import { useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  useCreateRole, useCreateUser, useDeleteRole, usePages, usePermissions, useRoles,
  useSetRolePages, useSetRolePermissions, useSetRolePortals, useSetUserActive,
  useSetUserPassword, useSetUserRoles, useUpdateRole, useUpdateUser, useUsers,
  useRetailerUsers, useSetRetailerUserActive, useSetRetailerUserPassword,
  useSetRetailerUserRoles, useUpdateRetailerUser, useCreateRetailerUser, useRetailers,
  useAssignableRoles,
} from '../api/hooks';
import { ApiError } from '../api/client';
import { useSession } from '../auth/session';
import {
  emailProblem, nameProblem, passwordProblem, phoneProblem, problemsByField,
} from './user-form';
import type { InternalUser, PageDef, PermissionDef, RetailerUser, Role } from '../api/types';
import {
  Alert, Button, Chip, Field, Modal, Note, PageHeader, Panel, Spinner, Table, Tabs, Tiles,
} from '../ui';
import { pctText } from '../format';

/**
 * A-06 and A-07 — the administration of who may do what.
 *
 * Every refusal on these screens is rendered from the server's own sentence
 * rather than a message invented here. SA-05, SA-06 and the page floor all fail
 * with a reason that names the rule, and restating them in the client would give
 * two texts to keep in step, of which only one is enforced.
 *
 * Creating and editing happen in a dialog. As inline panels they pushed the very
 * table they were editing off the screen, so you lost sight of the row you had
 * just clicked — and the role editor, which carries a permission matrix and a
 * screen list, pushed hardest.
 *
 * A dialog also settles a question the inline version left open. Each panel had
 * its own save button per section, which meant four ways to leave a role half
 * applied. Here there is one **Save changes**, and it applies what actually
 * changed in dependency order.
 */

/** Runs a mutation and keeps the server's refusal, which is the useful part. */
function useAction() {
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  /**
   * Returns void rather than a promise, because that is what it is: a click
   * handler with nobody to await it. Every outcome already lands in `error` or
   * `busy`, so handing back a promise would only invite a caller to drop it
   * silently — which is what an unhandled rejection in an event handler is.
   */
  const run = (fn: () => Promise<unknown>, after?: () => void): void => {
    setError(null);
    setFields({});
    setBusy(true);
    void (async () => {
      try {
        await fn();
        after?.();
      } catch (e) {
        /*
         * The server answers a refusal with a path per problem. Flattening that to one
         * sentence — "Validation failed" — threw away the only part that says WHICH field,
         * leaving the reader to guess among six inputs. Kept, so a form can put each reason
         * beside the thing it is about.
         */
        if (e instanceof ApiError) {
          setError(e.message);
          setFields(problemsByField(e.fieldErrors));
        } else {
          setError('That did not work');
          setFields({});
        }
      } finally {
        setBusy(false);
      }
    })();
  };
  return {
    error, busy, run, fields,
    clear: () => { setError(null); setFields({}); },
  };
}

const sameSet = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((x) => b.includes(x));

function Toggle(
  { on, onChange, label, hint, disabled }:
  { on: boolean; onChange: (v: boolean) => void; label: ReactNode; hint?: ReactNode; disabled?: boolean },
) {
  return (
    <label
      style={{
        display: 'flex', gap: 'var(--mk-space-2)', alignItems: 'baseline',
        padding: '2px 0', cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <input
        type="checkbox" checked={on} disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: 'auto', margin: 0 }}
      />
      <span>
        {label}
        {hint ? <><br /><span className="mk-hint">{hint}</span></> : null}
      </span>
    </label>
  );
}

function Row({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 'var(--mk-space-3)', flexWrap: 'wrap', alignItems: 'center' }}>
      {children}
    </div>
  );
}

/** A titled break inside a dialog body, where a nested Panel would be too heavy. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ marginTop: 'var(--mk-space-5)' }}>
      <div className="mk-lbl">{title}</div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------- users

/**
 * What a role actually lets someone do, in one line, beside the toggle that grants it.
 *
 * RL-12 fixes the three retailer roles and their meanings, so these are the requirement
 * rather than a gloss invented here. They matter at the moment of choosing: "Buyer" tells
 * somebody picking a role nothing about whether that person will be able to see the account's
 * invoices, and getting it wrong is only discovered by the customer.
 *
 * Internal roles are not listed. They are created at runtime by A-07 and their meaning is
 * whatever permissions somebody granted them this morning — a fixed sentence here would be a
 * second description, right on the day it was written and wrong afterwards.
 */
const RETAILER_ROLE_MEANING: Record<string, string> = {
  ret_owner: 'Everything, including inviting and removing the account’s own users (RL-13).',
  ret_buyer: 'Browse, order, My Inventory and report sales. No payables or statement.',
  ret_accounts: 'Payables, invoices, payments and the statement. Cannot order.',
};

/**
 * A-06 — every login that can reach this platform, in one list.
 *
 * Two kinds of account, deliberately one table. Miraki staff live in `mki_internal_users`
 * and trade customers in `mki_retailer_users`; they have different columns, different roles
 * and different routes behind them. What they have in common is the question somebody is
 * actually asking when they open this screen: *who is this person, can they sign in, and
 * what can they see*. Answering that across two screens would mean knowing which kind of
 * account you were looking for BEFORE you could look — and support, who is on the phone to
 * somebody reading an email address aloud, is exactly who does not know that yet.
 *
 * The Account column is what makes the merge legible: a trade login shows the business it
 * belongs to, a staff login shows Miraki. Two people with the same name are told apart by
 * the account beside them.
 *
 * The halves stay separate where they genuinely differ, and are NOT flattened into a
 * lowest common denominator:
 *
 *   - A staff account can be created here; a trade login cannot. RL-13 gives invitation to
 *     the retailer's own Owner, and a login invented by Miraki inside somebody else's
 *     account is not a thing this screen should be able to do.
 *   - A trade login's EMAIL cannot be edited here. It is the identity the customer signs in
 *     with and the address their notifications go to; changing it from this side would
 *     redirect their mail and lock them out of a login they still believe they hold.
 *   - The two sets of roles never mix. Internal roles are offered to staff and retailer
 *     roles to trade logins, because the page and permission catalogs are partitioned by
 *     audience — the wrong one would resolve to screens that do not exist on that side.
 *
 * The retailer half needs `retailer_user.manage`, which is separate from `user.manage` on
 * purpose: setting a customer's password is a larger thing than administering a colleague's.
 * Somebody holding only the latter sees this screen with the staff rows and no retailer
 * rows, rather than an error for a half they were never granted.
 */
export function Users() {
  const { can } = useSession();
  const mayManageRetailerUsers = can('retailer_user.manage');

  const { data: users, isLoading, error } = useUsers();
  const { data: retailerUsers, isLoading: loadingRetailer } = useRetailerUsers(mayManageRetailerUsers);
  /*
   * The ASSIGNABLE list, not the full one. `/roles` is behind `role.manage` — which this
   * screen's own audience does not hold — so asking for it answered 403 in the background
   * and left every Roles section blank with nothing on screen to say why.
   */
  const { data: roles } = useAssignableRoles();
  const setActive = useSetUserActive();
  const setRetailerActive = useSetRetailerUserActive();
  const act = useAction();
  const [creating, setCreating] = useState(false);
  const [creatingRetailer, setCreatingRetailer] = useState(false);
  /*
   * Which row is open, in the URL.
   *
   * Somewhere else in the product — a retailer's own Users tab — wants to send you to ONE
   * person, not to a list you then have to find them in. A link can only do that if the
   * screen reads who it was asked for, so `?user=<id>` opens that editor directly and the
   * link is something support can paste to each other.
   *
   * The state is still local: closing the dialog clears the parameter rather than pushing
   * history, so Back leaves the screen instead of stepping through dialogs.
   */
  const [params, setParams] = useSearchParams();
  const editing = params.get('user');
  const setEditing = (id: string | null) => setParams(id ? { user: id } : {}, { replace: true });
  /*
   * Suspended accounts are hidden until asked for.
   *
   * Nothing on this screen deletes a user — suspension IS the end state (SA-06 and the
   * audit trail both depend on the row staying), so every account that ever existed is
   * here forever, and the list only ever grows. On the shared development database that
   * was already nine dead probe accounts from a browser test, listed above the four
   * people who actually work here.
   *
   * Hidden, never dropped: the count below says how many there are and shows them in one
   * click, because the only way to REACTIVATE somebody is to find them first. A filter
   * that quietly loses the rows it filters would make that impossible.
   */
  const [showSuspended, setShowSuspended] = useState(false);

  if (isLoading || (mayManageRetailerUsers && loadingRetailer)) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;

  // Staff hold staff roles, trade logins hold retailer roles. The one table holds both and
  // each API refuses the other's, so neither set is ever offered to the wrong kind.
  const assignable = (roles ?? []).filter((r) => r.audience === 'INTERNAL' && r.isActive);
  const retailerAssignable = (roles ?? []).filter((r) => r.audience === 'RETAILER' && r.isActive);

  /*
   * One row type over two sources, the same shape the Retailers list uses for accounts and
   * applications. The discriminant is carried rather than sniffed from the fields, so a
   * column that must behave differently per kind says so, and TypeScript makes the other
   * branch impossible to forget.
   */
  type Row =
    | { kind: 'internal'; id: string; user: InternalUser }
    | { kind: 'retailer'; id: string; user: RetailerUser };

  const all: Row[] = [
    ...(users ?? []).map((u): Row => ({ kind: 'internal', id: `i:${u.id}`, user: u })),
    ...(retailerUsers ?? []).map((u): Row => ({ kind: 'retailer', id: `r:${u.id}`, user: u })),
  ].sort((a, b) => a.user.name.localeCompare(b.user.name));

  const suspended = all.filter((r) => !r.user.isActive).length;
  const rows = showSuspended ? all : all.filter((r) => r.user.isActive);
  /*
   * Matches either the row key or the underlying user id. The key is prefixed (`i:` / `r:`)
   * so the two kinds cannot collide in one list, but an incoming LINK knows only the id it
   * holds — a retailer's Users tab has a trade login's uuid and nothing about this screen's
   * row keys, and should not have to.
   */
  const open = all.find((r) => r.id === editing || r.user.id === editing);

  const staffCount = (users ?? []).filter((u) => u.isActive).length;
  const tradeCount = (retailerUsers ?? []).filter((u) => u.isActive).length;

  return (
    <>
      <PageHeader
        title="Users"
        actions={(
          <span style={{ display: 'inline-flex', gap: 'var(--mk-space-2)' }}>
            <Button tone="ghost" onClick={() => { act.clear(); setCreating(true); }}>
              New internal user
            </Button>
            {/*
              * Offered only to somebody who may actually do it. A button that always 403s is
              * worse than no button: it tells people the capability exists and that they are
              * broken, rather than that it is not theirs.
              */}
            {mayManageRetailerUsers ? (
              <Button onClick={() => { act.clear(); setCreatingRetailer(true); }}>
                New retailer login
              </Button>
            ) : null}
          </span>
        )}
      />
      {act.error ? <Alert tone="stop">{act.error}</Alert> : null}
      <Alert tone="them">
        <b>At least one active Super Admin must exist at all times.</b> Suspending the last one, or
        stripping its role, is refused by the service rather than merely disabled here — the rule
        holds whether the request comes from this screen, the API, or a script (SA-06). Only a
        Super Admin may grant Super Admin (SA-05).
      </Alert>

      <Tiles items={[
        { k: 'Miraki staff', v: String(staffCount) },
        ...(mayManageRetailerUsers ? [{ k: 'Trade logins', v: String(tradeCount) }] : []),
      ]} />

      <Panel>
        {/*
          * Shown only when something IS hidden. A permanent "0 suspended" line would be a
          * control that never does anything, on a screen most people open to do one thing.
          *
          * It states the number rather than just offering a switch, because the number is
          * the fact: "nine suspended accounts are hidden" explains the gap between this
          * list and the one somebody remembers, which a bare "Show suspended" does not.
          */}
        {suspended > 0 ? (
          <p className="mk-hidden-rows">
            {showSuspended
              ? `Showing ${suspended} suspended ${suspended === 1 ? 'account' : 'accounts'}.`
              : `${suspended} suspended ${suspended === 1 ? 'account is' : 'accounts are'} hidden.`}
            {' '}
            <button type="button" className="mk-btn-link" onClick={() => setShowSuspended(!showSuspended)}>
              {showSuspended ? 'Hide them' : 'Show them'}
            </button>
          </p>
        ) : null}
        <Table<Row>
          rows={rows}
          rowKey={(r) => r.id}
          filterLabel="Filter by name, email, account or role"
          /*
           * The ACCOUNT is in the filter text, which is most of the point of merging: support
           * is usually given a company name, not an email, and "lustre" should find every
           * login that can sign in to that account whichever kind it is.
           */
          filter={(r) => [
            r.user.name,
            r.user.email,
            r.kind === 'retailer' ? r.user.retailerName : 'Miraki',
            r.user.isActive ? 'active' : 'inactive',
            ...r.user.roles.map((x) => x.name),
          ].filter(Boolean).join(' ')}
          columns={[
            {
              header: 'Name',
              render: (r) => (
                <>
                  {r.user.name}
                  {r.kind === 'internal' && r.user.isSuperAdmin
                    ? <> <Chip tone="them">Super Admin</Chip></> : null}
                  {r.kind === 'retailer' && r.user.isPrimary
                    ? <> <Chip tone="run">Primary</Chip></> : null}
                </>
              ),
            },
            { header: 'Email', render: (r) => <span className="mk-dim">{r.user.email}</span> },
            {
              header: 'Account',
              /*
               * The column that makes one list out of two. A trade login names the business
               * it belongs to and links to it, because "who else is on this account" is the
               * next question after "who is this"; a staff login says Miraki, which is both
               * the account it belongs to and how you tell the two kinds apart without a
               * column spent on saying which kind it is.
               */
              render: (r) => (r.kind === 'retailer'
                ? <Link to={`/retailers/${r.user.retailerId}`}>{r.user.retailerName}</Link>
                : <span className="mk-dim">Miraki</span>),
            },
            {
              header: 'Roles',
              render: (r) => r.user.roles.map((x) => x.name).join(', ')
                || <span className="mk-dim">none</span>,
            },
            {
              header: 'State',
              render: (r) => (
                <Chip tone={r.user.isActive ? 'done' : 'stop'}>
                  {r.user.isActive ? 'Active' : 'Suspended'}
                </Chip>
              ),
            },
            {
              header: '', numeric: true,
              render: (r) => (
                <Row>
                  <Button size="sm" tone="ghost" onClick={() => { act.clear(); setEditing(r.id); }}>
                    Edit
                  </Button>
                  <Button
                    size="sm" tone="ghost"
                    disabled={setActive.isPending || setRetailerActive.isPending || act.busy}
                    /*
                     * Suspending REVEALS the suspended, rather than hiding what you just did.
                     *
                     * Without this the row vanishes the instant it is clicked — the account is
                     * now suspended and suspended rows are hidden — so the only feedback for a
                     * destructive-feeling action is the thing you were looking at disappearing.
                     * That reads as a mistake, and the natural response is to do it again to the
                     * next row down, which has moved up to where the cursor is.
                     */
                    onClick={() => {
                      const active = r.user.isActive;
                      if (active) setShowSuspended(true);
                      const id = r.kind === 'internal' ? r.user.id : r.user.id;
                      const args = {
                        id, active: !active,
                        ...(active ? { reason: 'Suspended from the admin console' } : {}),
                      };
                      void act.run(() => (r.kind === 'internal'
                        ? setActive.mutateAsync(args)
                        : setRetailerActive.mutateAsync(args)));
                    }}
                  >
                    {r.user.isActive ? 'Suspend' : 'Reactivate'}
                  </Button>
                </Row>
              ),
            },
          ]}
        />
      </Panel>

      {creating ? (
        <NewUser roles={assignable} onDone={() => setCreating(false)} />
      ) : null}

      {creatingRetailer ? (
        <NewRetailerUser roles={retailerAssignable} onDone={() => setCreatingRetailer(false)} />
      ) : null}

      {open?.kind === 'internal' ? (
        <EditUser
          key={open.id}
          user={open.user}
          roles={assignable}
          onDone={() => setEditing(null)}
        />
      ) : null}

      {open?.kind === 'retailer' ? (
        <EditRetailerUser
          key={open.id}
          user={open.user}
          roles={retailerAssignable}
          onDone={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}

function NewUser({ roles, onDone }: { roles: Role[]; onDone: () => void }) {
  const create = useCreateUser();
  const act = useAction();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [roleIds, setRoleIds] = useState<string[]>([]);

  const ready = !!email && name.length >= 2 && password.length >= 12;

  return (
    <Modal
      title="New user"
      onClose={onDone}
      footer={
        <>
          <Button
            disabled={act.busy || !ready}
            onClick={() => act.run(
              () => create.mutateAsync({ email, name, password, roleIds }),
              onDone,
            )}
          >
            Create user
          </Button>
          <Button tone="ghost" onClick={onDone}>Cancel</Button>
        </>
      }
    >
      {act.error ? <Alert tone="stop">{act.error}</Alert> : null}
      <div className="mk-fset">
        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} minLength={2} />
        </Field>
        <Field label="Email">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Password" hint="At least 12 characters. Replaced by an invite link when Notifications lands.">
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={12} />
        </Field>
      </div>

      <Section title="Roles">
        {roles.map((r) => (
          <Toggle
            key={r.id}
            on={roleIds.includes(r.id)}
            onChange={(on) => setRoleIds(on ? [...roleIds, r.id] : roleIds.filter((x) => x !== r.id))}
            label={<>{r.name} <span className="mk-dim">{r.code}</span></>}
            {...(r.isSuperAdmin ? { hint: 'Only a Super Admin may grant this (SA-05).' } : {})}
          />
        ))}
      </Section>
    </Modal>
  );
}

/**
 * Creating a login on a trade customer's account — RL-11, at that customer's request.
 *
 * The account is chosen FIRST and everything else follows from it, because every refusal
 * behind this dialog is about the account: whether it is closed (AL-04), and whether it
 * already has somebody who can administer it. Asking for the person before the account would
 * collect a name and an email and then refuse on a fact nobody had been shown.
 *
 * The Owner rule is surfaced rather than sprung. An account with no active Owner says so
 * under the picker, and the Owner toggle is pre-selected — the server refuses otherwise
 * (RL-13), and a dialog that lets you fill in four fields before telling you is a dialog that
 * wastes the attempt. The client has said this rule may be relaxed; when it is, the hint goes
 * and nothing else here has to change.
 */
export function NewRetailerUser(
  { roles, onDone, fixedRetailerId, fixedRetailerName }: {
    roles: Role[];
    onDone: () => void;
    /*
     * Opened FROM an account — its Users tab — so the account is already decided and the
     * picker would be a question with one answer. Absent on A-06, where choosing it is the
     * first real decision.
     */
    fixedRetailerId?: string;
    fixedRetailerName?: string;
  },
) {
  const create = useCreateRetailerUser();
  const { data: retailers } = useRetailers(!fixedRetailerId);
  const act = useAction();

  const [retailerId, setRetailerId] = useState(fixedRetailerId ?? '');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [roleIds, setRoleIds] = useState<string[]>([]);

  // Only the logins already on the chosen account matter, and only to answer one question:
  // does it have an Owner? Asked for one account rather than all of them.
  const { data: existing } = useRetailerUsers(!!retailerId, retailerId || undefined);
  const ownerRole = roles.find((r) => r.code === 'ret_owner');
  const hasOwner = (existing ?? []).some(
    (u) => u.isActive && u.roles.some((r) => r.code === 'ret_owner'),
  );
  const needsOwner = !!retailerId && !hasOwner;
  const ownerMissing = needsOwner && !!ownerRole && !roleIds.includes(ownerRole.id);

  // AL-04 — a closed account takes no new logins, so it is not offered as a choice.
  const open = (retailers ?? []).filter((r) => r.status !== 'CLOSED');

  /*
   * Checked as you type, shown once you have had a go.
   *
   * `touched` is what stops the form shouting at a field nobody has reached yet: a dialog
   * that opens already red has told you nothing and looks broken. A field speaks up when you
   * leave it, and from then on corrects itself live — which is the moment the answer is
   * useful, rather than after Create is pressed and a password has to be retyped.
   */
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const touch = (k: string) => () => setTouched((t) => ({ ...t, [k]: true }));

  const problems: Record<string, string | null> = {
    firstName: nameProblem(firstName, 'first name'),
    lastName: nameProblem(lastName, 'last name'),
    email: emailProblem(email),
    phone: phoneProblem(phone),
    password: passwordProblem(password),
    roleIds: roleIds.length === 0 ? 'Choose at least one role.' : ownerMissing
      ? 'This account has no Owner, so this login must be one.' : null,
  };
  // The server's word wins where it has one: it knows things this form cannot, such as
  // whether an address is already in use.
  const shown = (k: string) => act.fields[k] ?? (touched[k] ? problems[k] ?? null : null);
  const ready = !!retailerId && Object.values(problems).every((v) => v === null);

  const save = () => {
    // Pressing Create reveals everything, so a field never reached is still explained.
    setTouched({ firstName: true, lastName: true, email: true, phone: true, password: true, roleIds: true });
    if (!ready) return;
    act.run(
      () => create.mutateAsync({
        retailerId, firstName, lastName, email, password, roleIds,
        ...(phone.trim() ? { phone: phone.trim() } : {}),
      }),
      onDone,
    );
  };

  return (
    <Modal
      title={fixedRetailerName ? `New login — ${fixedRetailerName}` : 'New retailer login'}
      onClose={onDone}
      footer={
        <>
          <Button disabled={act.busy || !ready} onClick={save}>Create login</Button>
          <Button tone="ghost" onClick={onDone}>Cancel</Button>

        </>
      }
    >
      {act.error ? <Alert tone="stop">{act.error}</Alert> : null}

      {fixedRetailerId ? null : (
      <div className="mk-fset">
        <Field label="Account" hint="Closed accounts are not listed (AL-04).">
          <select
            value={retailerId}
            onChange={(e) => {
              setRetailerId(e.target.value);
              // The Owner rule depends on the account, so a change here invalidates a
              // choice made under the previous one.
              setRoleIds([]);
            }}
          >
            <option value="">Choose an account…</option>
            {open.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}{r.status === 'ACTIVE' ? '' : ` — ${r.status.toLowerCase().replace('_', ' ')}`}
              </option>
            ))}
          </select>
        </Field>
      </div>
      )}

      {needsOwner ? (
        <Note>
          <b>This account has no active Owner.</b> Its first login must hold the Owner role, or
          nobody there can invite, re-role or reactivate anyone afterwards (RL-13).
        </Note>
      ) : null}

      <div className="mk-fset">
        <Field label="First name" error={shown('firstName')}>
          <input value={firstName} onBlur={touch('firstName')} onChange={(e) => setFirstName(e.target.value)} />
        </Field>
        <Field label="Last name" error={shown('lastName')}>
          <input value={lastName} onBlur={touch('lastName')} onChange={(e) => setLastName(e.target.value)} />
        </Field>
        <Field label="Phone" error={shown('phone')}>
          <input value={phone} onBlur={touch('phone')} onChange={(e) => setPhone(e.target.value)} />
        </Field>
        <Field
          label="Email" hint="Identifies this person across the whole platform."
          error={shown('email')}
        >
          {/* `text`, not `email`. The browser's own bubble fires on submit saying "Please
              enter an email address" and covers the line below that says what is actually
              wrong with this one. One refusal, in one place. */}
          <input value={email} onBlur={touch('email')} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field
          label="Password" hint="At least 12 characters. They must change it at first sign-in."
          error={shown('password')}
        >
          <input
            type="password" value={password} onBlur={touch('password')}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
      </div>

      <Section title="Roles">
        {shown('roleIds') ? <p className="mk-field-err" role="alert">{shown('roleIds')}</p> : null}
        {roles.map((r) => (
          <Toggle
            key={r.id}
            on={roleIds.includes(r.id)}
            onChange={(on) => setRoleIds(on ? [...roleIds, r.id] : roleIds.filter((x) => x !== r.id))}
            label={<>{r.name} <span className="mk-dim">{r.code}</span></>}
            {...(RETAILER_ROLE_MEANING[r.code] ? { hint: RETAILER_ROLE_MEANING[r.code] } : {})}
          />
        ))}
      </Section>
    </Modal>
  );
}

/**
 * Editing a TRADE CUSTOMER's login. Deliberately not the same dialog as a staff one.
 *
 * Three differences from `EditUser`, each of them a rule rather than an omission:
 *
 *   - **No email field.** That address is the identity the customer signs in with and where
 *     every notification about their account goes. Changing it from Miraki's side would
 *     redirect their mail and lock them out of a login they still believe they hold. A
 *     customer who needs a different address asks their own Owner (RL-13). The API refuses
 *     it too, so this is not a UI-only courtesy.
 *   - **Retailer roles only.** Owner, Buyer and Accounts (RL-12). Internal roles live in the
 *     same table and are never offered here; the API refuses them, because the page and
 *     permission catalogs are partitioned by audience and the wrong one would resolve to
 *     screens that do not exist on this side.
 *   - **A password set here must be changed at next sign-in**, and the dialog says so.
 *     Whoever typed it knows it, and a support reset that stayed usable would leave a Miraki
 *     employee holding working credentials to a customer's account.
 */
function EditRetailerUser(
  { user, roles, onDone }: { user: RetailerUser; roles: Role[]; onDone: () => void },
) {
  const update = useUpdateRetailerUser();
  const setRoles = useSetRetailerUserRoles();
  const setPassword = useSetRetailerUserPassword();
  const act = useAction();

  /*
   * The parts, falling back to splitting the whole for rows written before the split. The
   * last word is the surname and everything before it the rest, which is the only guess that
   * is right more often than not — and it is a STARTING value in an editable field, so a
   * wrong guess costs a correction rather than a wrong record.
   */
  const parts = user.name.trim().split(/\s+/);
  const [firstName, setFirstName] = useState(user.firstName ?? parts.slice(0, -1).join(' ') ?? '');
  const [lastName, setLastName] = useState(user.lastName ?? (parts.length > 1 ? parts.at(-1)! : ''));
  const [phone, setPhone] = useState(user.phone ?? '');
  const [roleIds, setRoleIds] = useState<string[]>(user.roles.map((r) => r.id));
  const [password, setPasswordValue] = useState('');
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const touch = (k: string) => () => setTouched((t) => ({ ...t, [k]: true }));

  /*
   * The same rules as the create dialog, with ONE difference: a blank password here means
   * "leave the current one alone" rather than a missing required field. A form that demanded
   * a password to rename somebody would be unusable for the thing it is mostly used for.
   */
  const problems: Record<string, string | null> = {
    firstName: nameProblem(firstName, 'first name'),
    lastName: nameProblem(lastName, 'last name'),
    phone: phoneProblem(phone),
    password: passwordProblem(password, { required: false }),
  };
  const shown = (k: string) => act.fields[k] ?? (touched[k] ? problems[k] ?? null : null);
  const blocked = Object.values(problems).some((v) => v !== null);

  const heldRoles = user.roles.map((r) => r.id);
  const nameChanged = firstName !== (user.firstName ?? parts.slice(0, -1).join(' '))
    || lastName !== (user.lastName ?? (parts.length > 1 ? parts.at(-1)! : ''))
    || phone !== (user.phone ?? '');
  const rolesChanged = [...roleIds].sort().join() !== [...heldRoles].sort().join();
  const settingPassword = password.length > 0;
  const dirty = nameChanged || rolesChanged || settingPassword;

  // Same order and the same reason as EditUser: separate endpoints, each audited, so a
  // failure part way leaves the earlier steps applied and the dialog stays open over a
  // list that has already refreshed.
  const save = () => act.run(async () => {
    if (nameChanged) {
      await update.mutateAsync({
        id: user.id,
        body: { firstName, lastName, phone: phone.trim() || null },
      });
    }
    if (rolesChanged) await setRoles.mutateAsync({ id: user.id, roleIds });
    if (settingPassword) await setPassword.mutateAsync({ id: user.id, password });
  }, onDone);

  return (
    <Modal
      title={`Edit — ${user.name}`}
      onClose={onDone}
      footer={
        <>
          <Button disabled={act.busy || !dirty || blocked} onClick={save}>Save changes</Button>
          <Button tone="ghost" onClick={onDone}>Cancel</Button>

        </>
      }
    >
      {act.error ? <Alert tone="stop">{act.error}</Alert> : null}

      {/*
        * The standing explanation of WHY this screen exists has gone. It said the retailer's
        * own Owner manages their users and that support is here to rename people and let
        * locked-out ones in — true, and read once. After that it is a paragraph between the
        * reader and the four fields they came for, on every visit.
        *
        * What is kept is what this particular dialog cannot be guessed from: whose account
        * this login belongs to, which the title does not say, and why the email is locked.
        */}
      <p className="mk-hint">{user.retailerName}</p>

      <div className="mk-fset">
        <Field label="First name" error={shown('firstName')}>
          <input value={firstName} onBlur={touch('firstName')} onChange={(e) => setFirstName(e.target.value)} />
        </Field>
        <Field label="Last name" error={shown('lastName')}>
          <input value={lastName} onBlur={touch('lastName')} onChange={(e) => setLastName(e.target.value)} />
        </Field>
        <Field label="Phone" error={shown('phone')}>
          <input value={phone} onBlur={touch('phone')} onChange={(e) => setPhone(e.target.value)} />
        </Field>
        <Field label="Email" hint="Changed by the retailer's own Owner.">
          <input type="email" value={user.email} readOnly disabled />
        </Field>
      </div>

      <Section title="Roles">
        {roles.map((r) => (
          <Toggle
            key={r.id}
            on={roleIds.includes(r.id)}
            onChange={(on) => setRoleIds(on ? [...roleIds, r.id] : roleIds.filter((x) => x !== r.id))}
            label={<>{r.name} <span className="mk-dim">{r.code}</span></>}
            {...(RETAILER_ROLE_MEANING[r.code] ? { hint: RETAILER_ROLE_MEANING[r.code] } : {})}
          />
        ))}
      </Section>

      <Section title="Password">
        {/* Was a four-line paragraph restating what everyone already assumes about setting
            somebody's password. The only part that is NOT obvious — that the one you type is
            temporary — survives as the field's own hint, where it is read at the moment it
            matters rather than skipped above the form. */}
        <div className="mk-fset">
          <Field
            label="New password"
            hint="At least 12 characters. They must change it at next sign-in. Leave blank to keep the current one."
            error={shown('password')}
          >
            <input
              type="password" value={password}
              onBlur={touch('password')}
              onChange={(e) => setPasswordValue(e.target.value)}
            />
          </Field>
        </div>
      </Section>
    </Modal>
  );
}

function EditUser({ user, roles, onDone }: { user: InternalUser; roles: Role[]; onDone: () => void }) {
  const update = useUpdateUser();
  const setRoles = useSetUserRoles();
  const setPassword = useSetUserPassword();
  const act = useAction();

  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [roleIds, setRoleIds] = useState<string[]>(user.roles.map((r) => r.id));
  const [password, setPasswordValue] = useState('');

  const heldRoles = user.roles.map((r) => r.id);
  const detailsChanged = name !== user.name || email !== user.email;
  const rolesChanged = !sameSet(roleIds, heldRoles);
  const settingPassword = password.length > 0;
  const dirty = detailsChanged || rolesChanged || settingPassword;

  /**
   * One save, applied in order, and only for what actually changed.
   *
   * Each step is a separate endpoint with its own audit entry, so a failure part
   * way through leaves the earlier steps applied. That is why the dialog stays
   * open on failure rather than closing: the list behind it has already
   * refreshed, so what landed and what did not is visible rather than guessed.
   */
  const save = () => act.run(async () => {
    if (detailsChanged) await update.mutateAsync({ id: user.id, body: { name, email } });
    if (rolesChanged) await setRoles.mutateAsync({ id: user.id, roleIds });
    if (settingPassword) await setPassword.mutateAsync({ id: user.id, password });
  }, onDone);

  return (
    <Modal
      title={`Edit — ${user.name}`}
      onClose={onDone}
      footer={
        <>
          <Button disabled={act.busy || !dirty} onClick={save}>Save changes</Button>
          <Button tone="ghost" onClick={onDone}>Cancel</Button>
          {settingPassword && password.length < 12
            ? <span className="mk-hint">A password needs at least 12 characters.</span>
            : null}
        </>
      }
    >
      {act.error ? <Alert tone="stop">{act.error}</Alert> : null}

      <div className="mk-fset">
        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} minLength={2} />
        </Field>
        <Field label="Email">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
      </div>

      <Section title="Roles">
        {roles.map((r) => (
          <Toggle
            key={r.id}
            on={roleIds.includes(r.id)}
            onChange={(on) => setRoleIds(on ? [...roleIds, r.id] : roleIds.filter((x) => x !== r.id))}
            label={<>{r.name} <span className="mk-dim">{r.code}</span></>}
            {...(r.isSuperAdmin ? { hint: 'Only a Super Admin may grant or revoke this (SA-05).' } : {})}
          />
        ))}
      </Section>

      <Section title="Password">
        <Note>
          Setting a password here is the only recovery route until self-service reset is built
          (P-04, P-05). Leave it blank to change nothing. The audit log records that it happened
          and who did it, never the value.
        </Note>
        <div className="mk-fset">
          <Field label="New password" hint="At least 12 characters.">
            <input
              type="password" value={password}
              onChange={(e) => setPasswordValue(e.target.value)} minLength={12}
            />
          </Field>
        </div>
      </Section>
    </Modal>
  );
}

// ---------------------------------------------------------------------- roles

/** A-07 — roles, the permission catalog and the screen catalog. */
export function Roles() {
  const { data: roles, isLoading } = useRoles();
  const { data: permissions } = usePermissions();
  const { data: pages } = usePages();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);

  if (isLoading) return <Spinner />;

  const staffPermissions = (permissions ?? []).filter((p) => p.audience === 'INTERNAL');
  const open = (roles ?? []).find((r) => r.id === editing);

  return (
    <>
      <PageHeader
        title="Roles &amp; permissions"
        // The counts only. "Every action is gated by one (RL-05)" was a note to whoever is
        // building this, not to the person granting a role — they cannot act on it, and the
        // Alert below already tells them the thing they CAN act on.
        sub={`${permissions?.length ?? 0} permissions · ${pages?.length ?? 0} screens`}
        actions={<Button onClick={() => setCreating(true)}>New role</Button>}
      />
      <Alert tone="them">
        <b>Super Admin is not a row in the matrix.</b> It holds every permission implicitly — and
        resolves to every screen — including ones that do not exist yet. A seeded list would look
        identical today and silently fail the day a new permission ships (SA-01). That is why its
        columns read <b>all</b>, not a count.
      </Alert>
      <Alert tone="us">
        <b>Access is dynamic in the grant, fixed in the vocabulary (CR-51).</b> Tick screens and
        functions here and they take effect at the holder&rsquo;s next sign-in, with no release.
        What cannot be invented at runtime is the catalog itself: a permission no guard checks
        allows nothing, and a route with no component leads nowhere.
      </Alert>

      <Panel title="Roles">
        <Table<Role>
          rows={roles ?? []}
          rowKey={(r) => r.id}
          columns={[
            {
              header: 'Role',
              render: (r) => (
                <>
                  <b>{r.name}</b> <span className="mk-dim">{r.code}</span>
                  {r.isProtected ? <> <Chip tone="them">Protected</Chip></> : null}
                  {!r.isActive ? <> <Chip tone="lapse">Inactive</Chip></> : null}
                </>
              ),
            },
            {
              header: 'Side',
              render: (r) => (
                <Chip tone={r.audience === 'INTERNAL' ? 'us' : 'run'}>
                  {r.audience === 'INTERNAL' ? 'Staff' : 'Customer'}
                </Chip>
              ),
            },
            { header: 'Users', numeric: true, render: (r) => r.userCount },
            {
              header: 'Permissions', numeric: true,
              render: (r) => (r.permissions === 'all' ? <b>all</b> : <>{r.permissions.length}</>),
            },
            {
              header: 'Screens', numeric: true,
              render: (r) => (r.pageIds === 'all' ? <b>all</b> : <>{r.pageIds.length}</>),
            },
            {
              header: 'Discount ceiling', numeric: true,
              render: (r) => (r.discountCeilingPct
                ? pctText(r.discountCeilingPct)
                : <span className="mk-dim">—</span>),
            },
            {
              header: '', numeric: true,
              render: (r) => (r.isSuperAdmin ? <span className="mk-hint">Not editable</span> : (
                <Button size="sm" tone="ghost" onClick={() => setEditing(r.id)}>Edit</Button>
              )),
            },
          ]}
        />
      </Panel>

      {creating ? (
        <NewRole permissions={staffPermissions} onDone={() => setCreating(false)} />
      ) : null}

      {open ? (
        <EditRole
          key={open.id}
          role={open}
          permissions={permissions ?? []}
          pages={pages ?? []}
          onDone={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}

function NewRole({ permissions, onDone }: { permissions: PermissionDef[]; onDone: () => void }) {
  const create = useCreateRole();
  const act = useAction();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [ceiling, setCeiling] = useState('');
  const [held, setHeld] = useState<string[]>([]);

  return (
    <Modal
      title="New role"
      wide
      onClose={onDone}
      footer={
        <>
          <Button
            disabled={act.busy || code.length < 2 || name.length < 2}
            onClick={() => act.run(
              () => create.mutateAsync({
                code, name, audience: 'INTERNAL', permissions: held,
                discountCeilingPct: ceiling.trim() === '' ? null : ceiling.trim(),
              }),
              onDone,
            )}
          >
            Create role
          </Button>
          <Button tone="ghost" onClick={onDone}>Cancel</Button>
          <span className="mk-hint">{held.length} of {permissions.length} functions</span>
        </>
      }
    >
      {act.error ? <Alert tone="stop">{act.error}</Alert> : null}
      <div className="mk-fset">
        <Field label="Code" hint="Lower case, stable. Seeds and fixtures refer to it.">
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="operations_admin" />
        </Field>
        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} minLength={2} />
        </Field>
        <Field label="Discount ceiling %" hint="RL-07. Leave blank for none.">
          <input value={ceiling} onChange={(e) => setCeiling(e.target.value)} placeholder="7.5" />
        </Field>
      </div>

      <Note>
        A role is created staff-side with no screens. Grant its functions here, then reopen it to
        tick screens — a screen can only be granted once the role holds the permission it is
        useless without.
      </Note>

      <PermissionMatrix permissions={permissions} held={held} onChange={setHeld} />
    </Modal>
  );
}

/**
 * One option on one line: tick, name, and the description dimmed beside it.
 *
 * The description used to sit on its own line under every checkbox, which
 * tripled the height of a forty-two-permission form and buried the ticks — the
 * one thing a person is actually reading — in prose. It is cut with an ellipsis
 * and carried in full by the title attribute.
 */
function Opt(
  { on, onChange, name, desc, disabled }:
  { on: boolean; onChange: (v: boolean) => void; name: ReactNode; desc: string; disabled?: boolean },
) {
  return (
    <label className={`mk-opt ${disabled ? 'off' : ''}`} title={desc}>
      <input type="checkbox" checked={on} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="mk-opt-name">{name}</span>
      <span className="mk-opt-desc">{desc}</span>
    </label>
  );
}

/** A module's options, with the count and a way to take or drop the lot. */
function Module(
  { title, count, total, onAll, children, disabled }:
  {
    title: string; count: number; total: number;
    onAll: (all: boolean) => void; children: ReactNode; disabled?: boolean;
  },
) {
  return (
    <div className="mk-mod">
      <div className="mk-mod-head">
        <span className="mk-lbl">{title}</span>
        <span className="mk-hint">{count}/{total}</span>
        <span className="mk-spacer" />
        {/* Verbs, not states: "All"/"None" beside a count read as a label
            describing the module rather than as the thing you can click. */}
        {!disabled ? (
          <button type="button" className="mk-mod-all" onClick={() => onAll(count < total)}>
            {count < total ? 'Select all' : 'Clear'}
          </button>
        ) : null}
      </div>
      <div className="mk-opts">{children}</div>
    </div>
  );
}

function PermissionMatrix(
  { permissions, held, onChange, disabled }:
  {
    permissions: PermissionDef[]; held: string[];
    onChange: (next: string[]) => void; disabled?: boolean;
  },
) {
  const byModule = permissions.reduce<Record<string, PermissionDef[]>>((acc, p) => {
    (acc[p.module] ??= []).push(p);
    return acc;
  }, {});

  return (
    <div>
      {Object.entries(byModule).map(([module, perms]) => {
        const codes = perms.map((p) => p.code);
        const on = codes.filter((c) => held.includes(c));
        return (
          <Module
            key={module}
            title={module.replace(/_/g, ' ')}
            count={on.length}
            total={codes.length}
            {...(disabled ? { disabled: true } : {})}
            onAll={(all) => onChange(all
              ? [...new Set([...held, ...codes])]
              : held.filter((c) => !codes.includes(c)))}
          >
            {perms.map((p) => (
              <Opt
                key={p.code}
                on={held.includes(p.code)}
                {...(disabled ? { disabled: true } : {})}
                onChange={(v) => onChange(v ? [...held, p.code] : held.filter((c) => c !== p.code))}
                name={<code>{p.action}</code>}
                desc={p.description}
              />
            ))}
          </Module>
        );
      })}
    </div>
  );
}

function EditRole(
  { role, permissions, pages, onDone }:
  { role: Role; permissions: PermissionDef[]; pages: PageDef[]; onDone: () => void },
) {
  const { me } = useSession();
  const update = useUpdateRole();
  const setPermissions = useSetRolePermissions();
  const setPages = useSetRolePages();
  const setPortals = useSetRolePortals();
  const remove = useDeleteRole();
  const act = useAction();

  const mine = pages.filter((p) => p.audience === role.audience);
  const catalog = permissions.filter((p) => p.audience === role.audience);
  const grantable = new Set(catalog.map((p) => p.code));

  /**
   * Held grants, narrowed to what this side can actually hold.
   *
   * A role seeded before the audience partition was enforced can carry codes
   * from the other catalog. They enforce nothing, and sending them back would be
   * refused, so they are dropped from the form rather than shown as ticks the
   * matrix has no row for — which is what made this read "45 of 41".
   */
  const heldPerms = (role.permissions === 'all' ? [] : role.permissions).filter((c) => grantable.has(c));
  const heldPages = role.pageIds === 'all' ? [] : role.pageIds;

  const [name, setName] = useState(role.name);
  const [ceiling, setCeiling] = useState(role.discountCeilingPct ?? '');
  const [active, setActive] = useState(role.isActive);
  const [perms, setPerms] = useState<string[]>(heldPerms);
  const [pageIds, setPageIds] = useState<string[]>(heldPages);
  const [portalIds, setPortalIds] = useState<string[]>(role.portalIds);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [tab, setTab] = useState('details');

  // `Me` is a union: a retailer session has no portal list. Only staff reach this
  // screen at all, but the type says so rather than the route being trusted.
  const portals = me && 'portals' in me ? me.portals : [];

  const detailsChanged =
    name !== role.name || active !== role.isActive || ceiling !== (role.discountCeilingPct ?? '');
  const permsChanged = !sameSet(perms, heldPerms);
  const pagesChanged = !sameSet(pageIds, heldPages);
  const portalsChanged = !sameSet(portalIds, role.portalIds);
  const dirty = detailsChanged || permsChanged || pagesChanged || portalsChanged;

  /**
   * One save, and the order is load-bearing.
   *
   * Functions go before screens. A screen may only be granted to a role that
   * already holds the permission beneath it, so ticking `retailer.view` and
   * `A-03` together only works if the permission lands first. The reverse case
   * needs the same order: revoking a function makes the server drop the screens
   * that depended on it, and writing the screens afterwards restores exactly the
   * set the administrator asked for rather than the server's pruned version.
   */
  const save = () => act.run(async () => {
    if (detailsChanged) {
      await update.mutateAsync({
        id: role.id,
        body: {
          name, isActive: active,
          discountCeilingPct: ceiling.trim() === '' ? null : ceiling.trim(),
        },
      });
    }
    if (permsChanged) await setPermissions.mutateAsync({ id: role.id, permissions: perms });
    if (pagesChanged || permsChanged) await setPages.mutateAsync({ id: role.id, pageIds });
    if (portalsChanged) await setPortals.mutateAsync({ id: role.id, portalIds });
  }, onDone);

  return (
    <Modal
      title={`Edit — ${role.name}`}
      wide
      onClose={onDone}
      footer={confirmDelete ? (
        <>
          <span>Delete <b>{role.name}</b> permanently?</span>
          <Button
            disabled={act.busy}
            onClick={() => act.run(
              () => remove.mutateAsync({ id: role.id, reason: 'Deleted from the admin console' }),
              onDone,
            )}
          >
            Yes, delete
          </Button>
          <Button tone="ghost" onClick={() => setConfirmDelete(false)}>Keep it</Button>
        </>
      ) : (
        <>
          <Button disabled={act.busy || role.isProtected || !dirty} onClick={save}>
            Save changes
          </Button>
          <Button tone="ghost" onClick={onDone}>Cancel</Button>
          <span className="mk-spacer" />
          <Button
            tone="ghost"
            disabled={role.isProtected || role.userCount > 0}
            {...(role.userCount > 0 ? { title: `Held by ${role.userCount} user(s)` } : {})}
            onClick={() => setConfirmDelete(true)}
          >
            Delete role
          </Button>
        </>
      )}
    >
      {act.error ? <Alert tone="stop">{act.error}</Alert> : null}
      {role.isProtected ? (
        <Alert tone="them">
          This role is protected. It cannot be renamed, deactivated, or have its grants edited
          (SA-04) — the service refuses each of those regardless of what this screen offers.
        </Alert>
      ) : null}

      {/* Independent sections, so the counts on the strip double as the summary
          the single long column never had: you can see a role holds 38 of 42
          functions and 5 of 13 screens without scrolling through either. */}
      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'details', label: 'Details' },
          { id: 'functions', label: 'Functions', badge: `${perms.length}/${catalog.length}` },
          { id: 'screens', label: 'Screens', badge: `${pageIds.length}/${mine.length}` },
          ...(portals.length
            ? [{ id: 'portals', label: 'Portals', badge: `${portalIds.length}/${portals.length}` }]
            : []),
        ]}
      />

      {tab === 'details' ? (
        <>
          <div className="mk-fset">
            <Field label="Name">
              <input
                value={name} onChange={(e) => setName(e.target.value)}
                minLength={2} disabled={role.isProtected}
              />
            </Field>
            <Field label="Discount ceiling %" hint="RL-07. Blank for none.">
              <input
                value={ceiling} onChange={(e) => setCeiling(e.target.value)}
                disabled={role.isProtected} placeholder="7.5"
              />
            </Field>
          </div>
          <Toggle
            on={active} onChange={setActive} disabled={role.isProtected}
            label="Active"
            hint="An inactive role grants nothing, but stays visible with everyone who held it."
          />
          <Note style={{ marginTop: 'var(--mk-space-4)' }}>
            Code <code>{role.code}</code> · held by {role.userCount} user{role.userCount === 1 ? '' : 's'}.
            A role is deleted only when nobody holds it — deleting one out from under its holders
            would change what those people can do and record the role going rather than the access
            they lost.
          </Note>
        </>
      ) : null}

      {tab === 'functions' ? (
        <PermissionMatrix
          permissions={catalog} held={perms} onChange={setPerms}
          {...(role.isProtected ? { disabled: true } : {})}
        />
      ) : null}

      {tab === 'screens' ? (
        <>
          <Note style={{ marginBottom: 'var(--mk-space-4)' }}>
            A screen can only be granted to a role that already holds the permission beneath it.
            Tick the function first and the screen unlocks here, without saving in between.
          </Note>
          <div className="mk-opts">
            {mine.map((p) => {
              const floorMissing = p.permissionCode !== null && !perms.includes(p.permissionCode);
              return (
                <Opt
                  key={p.id}
                  on={pageIds.includes(p.id)}
                  {...(role.isProtected || floorMissing ? { disabled: true } : {})}
                  onChange={(on) => setPageIds(on ? [...pageIds, p.id] : pageIds.filter((x) => x !== p.id))}
                  name={<><span className="mk-dim">{p.id}</span> {p.name}</>}
                  desc={floorMissing
                    ? `Needs ${p.permissionCode}, which this role does not hold`
                    : p.permissionCode ?? 'Open to anyone signed in'}
                />
              );
            })}
          </div>
        </>
      ) : null}

      {tab === 'portals' ? (
        <>
          <Note style={{ marginBottom: 'var(--mk-space-4)' }}>
            RL-06 — which wholesale businesses this role reaches. Not the same question as what it
            may do there.
          </Note>
          <div className="mk-opts">
            {portals.map((p) => (
              <Opt
                key={p.id}
                on={portalIds.includes(p.id)}
                onChange={(on) => setPortalIds(on ? [...portalIds, p.id] : portalIds.filter((x) => x !== p.id))}
                name={p.name}
                desc={p.code}
              />
            ))}
          </div>
        </>
      ) : null}
    </Modal>
  );
}
